import React, { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Draggable } from "gsap/Draggable";
import "../aseets/css/GmSignalToOutcome.css";

gsap.registerPlugin(ScrollTrigger, Draggable);

const GmSignalToOutcome = () => {
  useEffect(() => {
    const signals = gsap.utils.toArray(".gm-signal-tag");
    const outcomes = gsap.utils.toArray(".gm-outcome");
    const outcomeTitles = gsap.utils.toArray(".gm-outcome h3");

    /* ===============================
       RAIN + RESOLUTION TIMELINE
    ================================ */
    const rainTL = gsap.timeline({
      scrollTrigger: {
        trigger: ".gm-signal",
        start: "top 65%",
        once: true,
      },
    });

    // SIGNAL RAIN (random timing)
    signals.forEach((signal) => {
      rainTL.fromTo(
        signal,
        {
          y: gsap.utils.random(-140, -80),
          x: gsap.utils.random(-140, 140),
          opacity: 0,
          rotate: gsap.utils.random(-12, 12),
        },
        {
          y: 260, // settles at bottom
          x: gsap.utils.random(-40, 40),
          opacity: 1,
          rotate: gsap.utils.random(-8, 8),
          duration: gsap.utils.random(1.2, 1.6),
          ease: "power2.in",
        },
        gsap.utils.random(0, 0.9)
      );
    });

    // OUTCOMES APPEAR
    rainTL.fromTo(
      outcomes,
      { y: 24, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.15,
        ease: "power3.out",
      },
      ">-0.3"
    );

    // EMPHASIS
    rainTL.add(() => {
      document
        .querySelector(".gm-outcomes")
        ?.classList.add("gm-outcomes-active");
    });

    // MICRO CONFIRMATION
    outcomeTitles.forEach((title, i) => {
      gsap.fromTo(
        title,
        { scale: 0.96 },
        {
          scale: 1,
          duration: 0.35,
          ease: "power2.out",
          delay: 0.12 * i,
        }
      );
    });

    /* ===============================
       DRAG + THROW (AFTER SETTLE)
    ================================ */
    rainTL.eventCallback("onComplete", () => {
      // Disable on touch devices
      if (window.matchMedia("(pointer: coarse)").matches) return;

      Draggable.create(signals, {
        type: "x,y",
        inertia: true,
        bounds: ".gm-signal",
        edgeResistance: 0.85,
        dragResistance: 0.15,
        zIndexBoost: false,

        onPress() {
          gsap.killTweensOf(this.target);
          gsap.to(this.target, {
            scale: 1.08,
            zIndex: 20,
            duration: 0.15,
            ease: "power2.out",
          });
        },

        onRelease() {
          const tag = this.target;

          // OPTION B — pause, then snap back
          gsap.delayedCall(0.2, () => {
            gsap.to(tag, {
              x: 0,
              y: 0,
              rotate: 0,
              scale: 1,
              zIndex: 1,
              duration: 0.8,
              ease: "elastic.out(1, 0.45)",
            });
          });
        },
      });
    });
  }, []);

  return (
    <section className="gm-signal">
      {/* SECTION TITLE */}
      <div className="gm-signal-header">
        <h2 className="gm-signal-title">
          Turning Signals into <span>Business Outcomes</span>
        </h2>
        <p className="gm-signal-subtitle">
          We filter digital noise into what truly matters — revenue, leads,
          visibility, and ROI.
        </p>
      </div>

      <div className="gm-signal-container">
        {/* SIGNALS */}
        <div className="gm-signal-cloud gm-signal-fall-zone">
          <span className="gm-signal-tag">#ad_click</span>
          <span className="gm-signal-tag">#seo_query</span>
          <span className="gm-signal-tag">#engagement</span>
          <span className="gm-signal-tag">#intent</span>
          <span className="gm-signal-tag">#impression</span>
          <span className="gm-signal-tag">#conversion</span>
          <span className="gm-signal-tag">#reach</span>
          <span className="gm-signal-tag">#traffic</span>
        </div>

        {/* OUTCOMES */}
        <div className="gm-outcomes">
          <div className="gm-outcome">
            <h3>Revenue</h3>
            <p>Growth that reflects on balance sheets</p>
          </div>

          <div className="gm-outcome">
            <h3>Leads</h3>
            <p>Qualified demand, not just numbers</p>
          </div>

          <div className="gm-outcome">
            <h3>Visibility</h3>
            <p>Presence where decisions begin</p>
          </div>

          <div className="gm-outcome">
            <h3>ROI</h3>
            <p>Measured performance, not assumptions</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GmSignalToOutcome;
